import { shareGame } from './66-game-sharing.mjs';
/** Live score view. Server eligibility/ranks are authoritative; every refresh
 * replaces both page and personal rank. Stale account or disposed card responses vanish. */
export function openCompetition(ctx, game, { allowed, start }) {
  const content = ctx.e('div', 'game-scores');
  const scopes = ctx.e('div', 'game-options'),
    periods = ctx.e('div', 'game-options');
  const explanation = ctx.e(
    'p',
    '',
    'Verified 2 minute runs on the same seed and rules. Positions can change.',
  );
  const status = ctx.e('p');
  status.setAttribute('role', 'status');
  const list = ctx.e('ol'),
    personal = ctx.e('p');
  const actions = ctx.e('div', 'game-options');
  let scope = ctx.state?.me ? 'friends' : 'everyone',
    period = 'all',
    serial = 0,
    board = null,
    cursor = null;
  const buttons = [];
  const button = (text, fn) => {
    const b = ctx.e('button', 'game-action', text);
    b.type = 'button';
    b.addEventListener('click', fn);
    return b;
  };
  for (const [value, label] of [
    ['friends', 'Friends'],
    ['everyone', 'Everyone'],
  ]) {
    const b = button(label, () => {
      scope = value;
      void load();
    });
    scopes.append(b);
    buttons.push([b, () => scope === value]);
  }
  for (const [value, label] of [
    ['week', 'This week'],
    ['all', 'All time'],
  ]) {
    const b = button(label, () => {
      period = value;
      void load();
    });
    periods.append(b);
    buttons.push([b, () => period === value]);
  }
  const around = button('Around me', () => void load({ around: true }));
  const next = button('Next page', () => void load({ cursor }));
  const retry = button('Refresh / Retry', () => void load());
  const play = button('Play this challenge', () => {
    if (!board || !allowed()) return;
    content.closest('dialog')?.close();
    void start(board.period, board.seed);
  });
  const share = button('Share challenge', () => {
    if (board && allowed()) void shareGame(ctx, game, board.seed, board.engineVersion, board);
  });
  actions.append(around, next, retry, play, share);
  content.append(
    ctx.e('h3', '', game.title + ' · Scores'),
    scopes,
    periods,
    explanation,
    status,
    personal,
    list,
    actions,
    ctx.e(
      'p',
      '',
      'Publishing is optional: Private, Anonymous or Public name in Settings. Friends includes accepted mutual friends. Anonymous names can still be inferred in very small groups. No scores are sent by opening this panel.',
    ),
  );
  ctx.dialog('Scores', content);
  async function load(options = {}) {
    const request = ++serial;
    board = null;
    cursor = null;
    list.replaceChildren();
    personal.textContent = '';
    play.disabled = true;
    share.disabled = true;
    next.disabled = true;
    around.disabled = !ctx.state?.me;
    for (const [b, selected] of buttons) b.setAttribute('aria-pressed', String(selected()));
    if (!allowed()) {
      status.textContent = 'This game or account is no longer available.';
      return;
    }
    if (scope === 'friends' && !ctx.state?.me) {
      status.textContent =
        'Sign in to see Friends scores. Everyone is available without signing in.';
      return;
    }
    status.textContent = 'Loading scores…';
    try {
      const query = new URLSearchParams({
        gameId: game.id,
        scope,
        period,
        around: String(Boolean(options.around)),
      });
      if (options.cursor) query.set('cursor', options.cursor);
      const result = await ctx.api('/api/games/leaderboard?' + query);
      if (request !== serial || !allowed() || !content.isConnected) return;
      board = result.board;
      cursor = result.nextCursor;
      status.textContent =
        board.label +
        (board.endsAt
          ? ` · Ends ${new Date(board.endsAt).toLocaleString()} (Monday 00:00 UTC)`
          : '') +
        ` · ${result.total} players`;
      personal.textContent = result.personal
        ? `Your rank: ${result.personal.rank} · ${result.personal.score} points`
        : 'No published result for you on this board.';
      if (result.target)
        personal.textContent += ` · Next target: rank ${result.target.rank}, ${result.target.pointsNeeded} more points to beat it.`;
      for (const row of result.entries) {
        const li = ctx.e('li', '', `${row.you ? 'You' : row.label} · ${row.score} points`);
        li.value = row.rank;
        list.append(li);
      }
      if (!result.entries.length)
        list.append(
          ctx.e(
            'li',
            '',
            'No eligible scores yet. Try the challenge privately or opt in before playing.',
          ),
        );
      play.disabled = false;
      share.disabled = false;
      next.disabled = !cursor;
    } catch (error) {
      if (request === serial && allowed() && content.isConnected)
        status.textContent = `Scores unavailable: ${error.message}. Choose Refresh / Retry.`;
    }
  }
  void load();
}
